import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocAlert,
  DocNavFooter,
  DocCard,
} from "@/components/docs";
import { CodeBlock } from "@/components/docs/api";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import { ClockIcon } from "@heroicons/react/24/outline";

export default function ApiRateLimitingPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "API Reference", href: "/docs/api" },
    { label: "Rate Limiting" },
  ];

  return (
    <DocLayout
      title="Rate Limiting - Uptime Monitor API Documentation"
      description="API rate limiting information for Uptime Monitor"
      canonical="https://uptime-monitor.io/docs/api/rate-limiting"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle title="Rate Limiting" description="Understand and work with API rate limits." />

      <section className="mb-12">
        <p className={`${docsColors.text.body} mb-6`}>
          The API uses a point-based rate limiting system to ensure fair usage. Rate limits are
          enforced per account (not per API key).
        </p>

        <DocAlert variant="info" className="mb-6">
          <p className="font-semibold mb-2">Rate Limit: 5000 points per hour per account</p>
          <p>
            The limit uses a sliding 1-hour window and applies across all API keys for an account.
          </p>
        </DocAlert>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={`${docsComponents.card} p-4`}>
            <div className="flex items-center mb-2">
              <ClockIcon className="h-5 w-5 text-blue-500 mr-2" />
              <h3 className="font-semibold">Single Resource GET</h3>
            </div>
            <p className={`${docsColors.text.muted} text-sm`}>Retrieving a single resource by ID</p>
            <p className="text-2xl font-bold text-blue-600 mt-2">1 point</p>
          </div>

          <div className={`${docsComponents.card} p-4`}>
            <div className="flex items-center mb-2">
              <ClockIcon className="h-5 w-5 text-green-500 mr-2" />
              <h3 className="font-semibold">Write Operations</h3>
            </div>
            <p className={`${docsColors.text.muted} text-sm`}>POST, PUT, PATCH, DELETE</p>
            <p className="text-2xl font-bold text-green-600 mt-2">3 points</p>
          </div>

          <div className={`${docsComponents.card} p-4`}>
            <div className="flex items-center mb-2">
              <ClockIcon className="h-5 w-5 text-purple-500 mr-2" />
              <h3 className="font-semibold">List/Search Requests</h3>
            </div>
            <p className={`${docsColors.text.muted} text-sm`}>
              Retrieving multiple resources or searching
            </p>
            <p className="text-2xl font-bold text-purple-600 mt-2">3 points</p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>
          Rate Limit Headers
        </h2>
        <p>All API responses include headers with current rate limit information:</p>

        <CodeBlock language="http">
          {`HTTP/1.1 200 OK
X-RateLimit-Limit: 5000
X-RateLimit-Remaining: 4994
X-RateLimit-Reset: 1704070800`}
        </CodeBlock>

        <div className="mt-4 space-y-2">
          <div>
            <code className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
              X-RateLimit-Limit
            </code>{" "}
            - Total points allowed per hour
          </div>
          <div>
            <code className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
              X-RateLimit-Remaining
            </code>{" "}
            - Points remaining in current window
          </div>
          <div>
            <code className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
              X-RateLimit-Reset
            </code>{" "}
            - Unix timestamp when the rate limit resets
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>
          Rate Limit Exceeded
        </h2>
        <p>
          When you exceed the rate limit, the API returns a{" "}
          <code className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
            429 Too Many Requests
          </code>{" "}
          response:
        </p>

        <CodeBlock language="http">
          {`HTTP/1.1 429 Too Many Requests
X-RateLimit-Limit: 5000
X-RateLimit-Remaining: 0
X-RateLimit-Reset: 1704070800
Retry-After: 3600`}
        </CodeBlock>

        <p className="mt-4">Response body:</p>

        <CodeBlock language="json">
          {`{
  "error": "rate_limit_exceeded",
  "message": "Too many requests. Please retry later.",
  "retry_after": 3600
}`}
        </CodeBlock>

        <DocAlert variant="warning" className="mt-4">
          The{" "}
          <code className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
            Retry-After
          </code>{" "}
          header indicates how many seconds to wait before retrying.
        </DocAlert>
      </section>

      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>Best Practices</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900">Handle Rate Limits Gracefully</h3>
            <p className="text-gray-600 mt-1">
              Always check for 429 responses and implement exponential backoff when retrying
              requests.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900">Monitor Your Usage</h3>
            <p className="text-gray-600 mt-1">
              Track the{" "}
              <code className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
                X-RateLimit-Remaining
              </code>{" "}
              header to avoid hitting limits.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900">Optimize List Requests</h3>
            <p className="text-gray-600 mt-1">
              Use pagination with reasonable page sizes. Each list request costs 3 points regardless
              of result count, so prefer fewer, larger pages over many small ones.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900">Cache When Possible</h3>
            <p className="text-gray-600 mt-1">
              Cache responses on your end to reduce API calls, especially for data that doesn&apos;t
              change frequently.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>
          Example: Handling Rate Limits
        </h2>
        <p>Here&apos;s an example of handling rate limits with exponential backoff:</p>

        <CodeBlock language="javascript">
          {`async function apiRequest(url, options = {}) {
  const maxRetries = 3;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': \`Bearer \${API_KEY}\`,
        ...options.headers
      }
    });
    
    // Check rate limit headers
    const remaining = response.headers.get('X-RateLimit-Remaining');
    const reset = response.headers.get('X-RateLimit-Reset');
    
    console.log(\`Rate limit remaining: \${remaining}\`);
    
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : Math.pow(2, retryCount) * 1000;
      
      console.log(\`Rate limited. Waiting \${waitTime}ms before retry...\`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      retryCount++;
      continue;
    }
    
    return response;
  }
  
  throw new Error('Max retries exceeded');
}`}
        </CodeBlock>
      </section>

      <DocNavFooter
        prev={{ href: "/docs/api/status-pages", text: "Status Pages API" }}
        next={{ href: "/docs/cli", text: "CLI Tool" }}
      />
    </DocLayout>
  );
}
